import { z } from 'zod/v4';
import { zodBigNumber } from '~/schemas/custom-schemas';
import { countryEnum, subsidyRegimeEnum, technologyEnum } from '~/schemas/enums';
import { gridData } from '~/schemas/grid';
import { dealTrancheVolumeTypeEnum } from './deals.schemas';
import {
  createMatchingAndPlottingResolutionGroups,
  createMetricsTableRequest,
  createMetricsTableResponse,
  metricsControlKeysEnum,
  nestedStringArrayFilter,
  periodStartFilter,
  stringArrayFilter,
  stringValueFilter,
} from './metrics.schemas';

// Supply table schemas
const supplyTableLabel = z.union([
  z.literal('supplyMetricsSummary'),
  z.literal('supplyDurations'),
  z.literal('supplyMatchingOverTime'),
  z.literal('supplyMatchingAverage'),
  z.literal('pivotTable'),
]);
const supplyColumns = z.union([
  // Groups
  z.literal('period_start'),
  z.literal('production_device_client_reference'),
  z.literal('production_device_name'),
  z.literal('production_device_registry_identifier'),
  z.literal('technology'),
  z.literal('country'),
  z.literal('supra_national_region'),
  z.literal('sub_national_region'),
  z.literal('certificate_label'),
  z.literal('certificate_type'),
  z.literal('subsidy_regime'),
  z.literal('deal_client_reference'),
  z.literal('deal_tranche_volume_type'),
  z.literal('counterparty_client_reference'),
  z.literal('counterparty_name'),
  z.literal('supply_type'),
  z.literal('supply_contract_type'),
  z.literal('duration'),
  z.literal('max_delivery_date_year_and_month'),
  // Metrics
  z.literal('supply'),
  z.literal('supply_allocated'),
  z.literal('supply_unallocated'),
]);

export type SupplyColumn = z.infer<typeof supplyColumns>;

const supplyTableRequest = createMetricsTableRequest(supplyColumns, supplyTableLabel);
const supplyTableResponse = createMetricsTableResponse(supplyColumns, supplyTableLabel);

// Allocated supply schemas
const allocatedSupplyTableLabel = z.union([
  z.literal('supplyMatchingOverTimeByAllocated'),
  z.literal('totalsByAllocated'),
  z.literal('supplyMatchingAverageByAllocated'),
]);
const allocatedSupplyColumns = z
  .union([
    z.literal('period_start'),
    z.literal('allocated_consumer_client_reference'),
    z.literal('allocated_consumer_name'),
    z.literal('supply_allocated'),
  ])
  .or(z.string());
export type AllocatedSupplyColumn = z.infer<typeof allocatedSupplyColumns>;

const allocatedSupplyTableRequest = createMetricsTableRequest(allocatedSupplyColumns, allocatedSupplyTableLabel);
const allocatedSupplyTableResponse = createMetricsTableResponse(allocatedSupplyColumns, allocatedSupplyTableLabel);

export type SupplyView = z.infer<typeof supplyTableResponse>['views'][number];
export type AllocatedSupplyView = z.infer<typeof allocatedSupplyTableResponse>['views'][number];

// Supply plotting resolution schemas

const combinedSupplyColumns = z.union([...supplyColumns.options, ...allocatedSupplyColumns.options]);
const supplyPlottingResolutionGroups = createMatchingAndPlottingResolutionGroups(combinedSupplyColumns);

export type SupplyGroups = z.infer<typeof supplyPlottingResolutionGroups>;

export const supplyFilters = z.object({
  certificate_label: nestedStringArrayFilter.optional(),
  certificate_type: stringArrayFilter.optional(),
  counterparty_id: stringArrayFilter.optional(),
  country: nestedStringArrayFilter.optional(),
  deal_id: stringArrayFilter.optional(),
  deal_tranche_id: stringArrayFilter.optional(),
  deal_tranche_volume_type: stringArrayFilter.optional(),
  minimum_commercial_operation_date: stringValueFilter.optional(),
  period_start: periodStartFilter,
  production_device_capacity: stringValueFilter.optional(),
  production_device_id: stringArrayFilter.optional(),
  registry: stringArrayFilter.optional(),
  registry_account_holder_id: stringArrayFilter.optional(),
  registry_account_id: stringArrayFilter.optional(),
  registry_transaction_id: stringArrayFilter.optional(),
  sub_national_region: nestedStringArrayFilter.optional(),
  subsidy_regime: nestedStringArrayFilter.optional(),
  supply_contract_type: stringArrayFilter.optional(),
  supply_duration: stringValueFilter.optional(),
  supply_type: stringArrayFilter.optional(),
  supra_national_region: nestedStringArrayFilter.optional(),
  technology: nestedStringArrayFilter.optional(),
});

export type SupplyFilters = z.infer<typeof supplyFilters>;
export type SupplyFilterKeys = keyof typeof supplyFilters.shape;

export const supplyFilterKeys = Object.keys(supplyFilters.shape) as Array<SupplyFilterKeys>;

export const supplyFilterHashKeysMap = {
  groups: metricsControlKeysEnum.enum.breakdown_by,
};

// Supply-view request & response
export const supplyRequestSchema = z.object({
  filters: supplyFilters,
  tables: z.object({
    allocated_supply: allocatedSupplyTableRequest.optional(),
    supply: supplyTableRequest,
  }),
});

export type SupplyRequest = z.infer<typeof supplyRequestSchema>;

export const supplyResponseSchema = z.object({
  tables: z.object({
    allocated_supply: allocatedSupplyTableResponse.optional(),
    supply: supplyTableResponse,
  }),
});

export type SupplyResponse = z.infer<typeof supplyResponseSchema>;

const supplyGridDataSchema = z.object({
  certificate_label: z.array(z.string()).nullable().optional(),
  certificate_type: z.string().nullable().optional(),
  counterparty_client_reference: z.string().nullable().optional(),
  counterparty_name: z.string().nullable().optional(),
  deal_client_reference: z.string().nullable().optional(),
  deal_tranche_client_reference: z.string().nullable().optional(),
  deal_tranche_volume_type: dealTrancheVolumeTypeEnum.nullable().optional(),
  generation_country: z.array(countryEnum).nullable().optional(),
  max_delivery_month: z.number().int().nullable().optional(),
  max_delivery_year: z.number().int().nullable().optional(),
  minimum_commercial_operation_date_month: z.number().int().nullable().optional(),
  minimum_commercial_operation_date_year: z.number().int().nullable().optional(),
  period_start_month: z.number().int().nullable().optional(),
  period_start_year: z.number().int().nullable().optional(),
  production_device_client_reference: z.string().nullable().optional(),
  production_device_name: z.string().nullable().optional(),
  production_device_registry_identifier: z.string().nullable().optional(),
  quantity_allocated: zodBigNumber.nullable().optional(),
  registry: z.string().nullable(),
  registry_account_holder_id: z.uuid().nullable(),
  registry_account_holder_identifier: z.string().nullable(),
  registry_account_holder_name: z.string().nullable(),
  registry_account_id: z.uuid().nullable(),
  registry_account_identifier: z.string().nullable(),
  registry_account_name: z.string().nullable(),
  sub_national_region: z.array(z.string()).nullable().optional(),
  subsidy_regime: z.array(subsidyRegimeEnum).nullable().optional(),
  supply_contract_type: z.string().nullable().optional(),
  supply_quantity: zodBigNumber.nullable().optional(),
  supply_type: z.string().nullable().optional(),
  supra_national_region: z.array(z.string()).nullable().optional(),
  technology: z.array(technologyEnum).nullable().optional(),
});

export type SupplyGridMetricsData = z.infer<typeof supplyGridDataSchema>;

// Plotting resolution request
export const supplyPlottingResolutionsRequestSchema = z.object({
  filters: supplyFilters,
  groups: supplyPlottingResolutionGroups,
});

export type SupplyPlottingResolutionsRequest = z.infer<typeof supplyPlottingResolutionsRequestSchema>;

export const supplyPivotTableResponseSchema = gridData(
  z.object({
    count: z.number(),
    data: z.array(supplyGridDataSchema.loose()), // loose to allow for tags
  }),
);
